import { useContext, useMemo } from 'react'
import CurrencyContext from 'contexts/currencyContext'
import { shallowEqual, useAppSelector } from 'hooks/reduxHooks'
import { arrayToMap } from 'lib/array/arrayUtils'
import {
  getCartPanelExperienceTimesKey,
  resolveExperiencePricing,
  type CartPanelExperienceTicket,
  type ExperienceResolvedPricing,
} from '../utils/cartPanelUtils'

export interface ExperiencePricingInput {
  tripItemId: number
  experienceId: string
  date?: string
  time?: string
  pickupPointId?: string
  redemptionLocationId?: string
  isBuyNowBookLater: boolean
  tickets: Array<CartPanelExperienceTicket>
  savedPrice?: number
  savedMemberPrice?: number
  savedValue?: number
}

export function useExperienceCartItemPricing(
  items: Array<ExperiencePricingInput>,
): Map<number, ExperienceResolvedPricing> {
  const currency = useContext(CurrencyContext)
  const { experiences, fetchingExperiences, experienceErrors, experienceTimes } = useAppSelector(state => ({
    experiences: state.experience.experiences,
    fetchingExperiences: state.experience.fetchingExperiences,
    experienceErrors: state.experience.experienceErrors,
    experienceTimes: state.experience.experienceTimes,
  }), shallowEqual)

  return useMemo(() => {
    return arrayToMap(items, item => item.tripItemId, item => {
      const experience = experiences[item.experienceId]
      const key = getCartPanelExperienceTimesKey(item, currency)
      const timeSlotState = experienceTimes[item.experienceId]?.[key]
      const loading = !!fetchingExperiences[item.experienceId] || !!timeSlotState?.fetching
      const error = !!experienceErrors[item.experienceId] || !!timeSlotState?.error

      return resolveExperiencePricing({
        experience,
        timeSlots: timeSlotState?.slots,
        tickets: item.tickets,
        date: item.date,
        time: item.time,
        isBuyNowBookLater: item.isBuyNowBookLater,
        savedPrice: item.savedPrice,
        savedMemberPrice: item.savedMemberPrice,
        savedValue: item.savedValue,
        loading,
        error,
      })
    })
  }, [items, experiences, fetchingExperiences, experienceErrors, experienceTimes, currency])
}
